<template>
    <div class="add-sites container">
        <header>
            <button class="back-caret" @click.prevent="close" aria-label="Done adding sites">
                <SvgsBackCaret />
            </button>
            <h2 class="subhead-2">Edit Itinerary</h2>
        </header>
        <section class="main-content">
            <h3 class="headline-1">Add Sites</h3>
            <div class="filters">
                <button v-if="!showFilters" type="button" class="show-filters" @click="toggleFilters" ref="filterToggle" aria-label="Filters">
                    <SvgsFilters class="filter-icon" />
                </button>
                <!-- `close` is the only event FiltersSites actually emits — Apply,
                     Reset and the X all route through filtersToQuery() to it.
                     `updateSites` and `resetSites` are declared on that component
                     but never emitted, so binding only those left every button in
                     the panel inert (Asana O2-54). Templates/Sites/page.vue binds
                     @close for the same reason. -->
                <GlobalFlyout v-show="showFilters" @toggleFilters="toggleFilters" :showing="showFilters">
                    <FiltersSites :sites="sites" :ohcFavoriteSites="userSites" @close="toggleFilters" @updateSites="updateSites" @resetSites="resetSites" />
                </GlobalFlyout>
                <!-- The label is visually hidden once there is text, not
                     removed: v-show took the input's only name away. -->
                <div class="form-field search" role="search" v-show="!showFilters">
                    <label :for="searchInputId" :class="{ 'sr-only': searchTerm !== '' }">Search sites</label>
                    <SvgsSearch />
                    <input :id="searchInputId" type="search" v-model="searchTerm" />
                </div>
            </div>
            <BlocksSitesListStack v-show="!showFilters" :showHeader="false" :cards="filteredSites" :itinerary="itinerary" @updateSingleItem="updateSingleItem" :isGhost="true" />
            <div v-if="filteredSites <= 0">
                <h2 class="no-results subhead-2">No sites match these filters.</h2>
            </div>
            <ItinerariesAddedSiteAlert v-if="siteNameForAlert !== null" @closeAddedSiteAlert="closeAlert" :siteName="siteNameForAlert" :listName="itinerary.name" />
        </section>

        <!-- Asana: "the Cancel and Apply buttons do not appear at bottom of menu
             (or anywhere else). So you can't save sites to an itinerary this way,
             and your work selecting sites gets lost."

             The edit view's Apply/Cancel bar is deliberately hidden while this
             panel is open (see edit.vue) because two fixed bars at z-index 9999
             overlapped and made the filter panel's own buttons unclickable —
             O2-54. That left the back caret as the only way forward: an
             unlabelled arrow in the top corner that reads as "cancel", while the
             place everyone looks for a confirm button is the bottom.

             The work was never actually lost — closing returns to the edit view
             with the additions pending and Apply enabled. But a control that
             gives no sign it worked is indistinguishable from one that failed,
             so people stopped before reaching Apply.

             A flex sibling, NOT another position:fixed bar: .add-sites is
             already a flex column whose .main-content takes the leftover height,
             so this pins to the bottom of the panel without entering the
             z-index fight that caused O2-54. Hidden with the site list while
             the filter panel is up, so it can never sit under the filter's own
             Apply/Reset bar. -->
        <div class="done-bar" v-show="!showFilters">
            <p class="site-count body-book" aria-live="polite">{{ pendingCount }} {{ pendingCount === 1 ? 'site' : 'sites' }} in this itinerary</p>
            <button type="button" @click.prevent="close" class="apply-cancel-buttons apply-button white-to-turquoise active">Done</button>
        </div>
    </div>
</template>

<script setup>
    // Composables
    const { getSiteNeighborhoodName } = useUtils();

    // Stores
    import { storeToRefs } from 'pinia';
    import { useRouteQuery } from '@vueuse/router';
    import { useLocationStore } from '@/stores/location';
    import { useNeighborhoodsStore } from '@/stores/neighborhoods';
    const neighborhoodsStore = useNeighborhoodsStore();
    import { useSitesStore } from '@/stores/sites';
    const sitesStore = useSitesStore();
    import { useUserStore } from '@/stores/user';
    // The user's favourited site ids, same source the sites page reads. Without
    // these the "My Favorites" sort short-circuits (Asana O2-54).
    const { sites: userSites } = storeToRefs(useUserStore());

    // The in-progress itinerary, the same working copy edit.vue's `listChanged`
    // compares and Apply writes. Read here only to show how many sites are in it
    // — the count is the feedback that a tap on + did something.
    const { ghostItinerary } = storeToRefs(useUserStore());
    const pendingCount = computed(() => ghostItinerary.value?.sites?.length ?? 0);

    // Emits
    const emit = defineEmits(['closeAddSites', 'test']);

    // Props
    const props = defineProps({
        itinerary: {
            type: Object,
            required: false,
            default: {},
        },
    });
    const { itinerary } = toRefs(props);

    // Variables
    const { $listen } = useNuxtApp();
    const neighborhoodsMap = neighborhoodsStore.neighborhoodsMap;
    const sites = sitesStore.sitesData;
    const siteNameForAlert = ref(null);
    const showFilters = ref(false);
    const searchTerm = ref('');
    const sitesSource = ref(sites);

    // Computed
    // The filter panel writes its choices to the route query, so this reads them
    // back the same way the sites page does. It used to apply the search term and
    // nothing else, which is why the panel opened, closed and changed nothing
    // (Asana O2-54). Shared pipeline so the two surfaces cannot drift.
    const siteTypeFilters = useRouteQuery('siteTypeFilters', []);
    const amenitiesFilters = useRouteQuery('amenitiesFilters', []);
    const datesFilters = useRouteQuery('datesFilters', '');
    const nearMe = useRouteQuery('nearMe', false);
    const sortBy = useRouteQuery('sortBy', 'alpha');

    const locationStore = useLocationStore();

    const filteredSites = computed(() => filterAndSortSites({
        sites: sitesSource.value,
        searchTerm: searchTerm.value,
        getNeighborhoodName: getSiteNeighborhoodName,
        neighborhoodsMap,
        siteTypeFilters: siteTypeFilters.value,
        amenitiesFilters: amenitiesFilters.value,
        datesFilters: datesFilters.value,
        nearMe: nearMe.value,
        sortBy: sortBy.value,
        coords: locationStore.coords,
        calculateDistance: (lat, lng) => locationStore.calculateDistance(lat, lng),
        userSites: userSites?.value ?? [],
    }));

    // Methods
    function close() {
        emit('closeAddSites');
    }

    function updateSingleItem(item) {
        emit('updateSingleItem', JSON.stringify(item));
    }


    const searchInputId = useId();

    function toggleFilters() {
        if (searchTerm.value !== '') {
            searchTerm.value = '';
        }
        showFilters.value = !showFilters.value;
    }

    function closeAlert() {
        siteNameForAlert.value = null;
        return siteNameForAlert;
    }

    function updateSites(val) {
        sitesSource.value = val;
        showFilters.value = false;
    }

    function resetSites() {
        sitesSource.value = sites;
    }

    // Lifecycle
    onMounted(() => {
        if (import.meta.client) {
            $listen('refreshItineraryList', (val) => {
                if (!val) return;

                if (val.list === 'ohcItineraries') {
                    siteNameForAlert.value = val?.name || '';
                }
            });
        }
    });
</script>

<style lang="scss" scoped>
    .add-sites {
        z-index: $zStepThree;
        position: fixed;
        top: 59px;
        right: 0;
        bottom: 0;
        left: 0;

        padding: 36px 0 0 0;
        background-color: $white;

        // Column so the grid can take whatever height is left over. It used to be
        // `height: calc(100% - 17svh)` against an 80svh panel, which hard-coded
        // the scroll area shorter than its parent and left the grid visibly cut
        // off with dead white space beneath it.
        display: flex;
        flex-direction: column;

        .main-content {
            flex: 1 1 auto;
            // Without this a flex child refuses to shrink below its content and
            // scrolls the page instead of itself.
            min-height: 0;
            overflow: hidden;
            overflow-y: auto;
            padding-bottom: 32px;

            // Every other child here sets its own gutter — the heading, the
            // filter row, the back caret all use 16px / 64px. The site grid was
            // the one that didn't, so it ran to the panel's edges while the rest
            // sat inset (Asana O2-44). Gutter it the same way rather than padding
            // the panel, which would double up on all the others.
            :deep(.stack) {
                padding-left: 16px;
                padding-right: 16px;

                @media (min-width: $tablet) {
                    padding-left: 64px;
                    padding-right: 64px;
                }
            }
        }

        // Nested under .add-sites, not header: it sat inside the header rule
        // while the bar is a sibling of the header, so none of this matched and
        // the count rendered as a stray unstyled line (O2-54).
        .done-bar {
            flex: 0 0 auto;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 16px;
            // The panel is pinned to the bottom of the screen on phones, so
            // the bar has to clear the iPhone home indicator.
            padding: 16px 16px calc(16px + env(safe-area-inset-bottom));
            background-color: $white;
            box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.1);

            .site-count {
                margin: 0;
            }

            @media (min-width: $tablet) {
                padding: 16px 64px;
            }
        }

        @media (min-width: $tablet) {
            width: 70%;
            max-width: $hd;
            margin: auto;
            height: 80svh;
            bottom: 0px;
            top: 0px;
            padding: 0;
        }
    }

    header {
        position: relative;
        text-align: center;
        margin-bottom: 34px;

        .reset {
            position: absolute;
            right: 16px;
            top: 0px;
        }

        .back-caret {
            z-index: 305;
            position: absolute;
            left: 16px;
            top: 0px;
        }

        @media (min-width: $tablet) {
            padding: 64px 0 0 0;

            .back-caret {
                left: 64px;
                top: 64px;
            }

            .reset {
                right: 64px;
                top: 64px;
            }
        }
    }

    h3.headline-1 {
        padding: 0 16px;

        @media (min-width: $tablet) {
            padding: 0 64px;
        }
    }

    .filters {
        display: flex;
        margin-top: 20px;
        margin-bottom: 20px;
        padding: 0 16px;

        @media (min-width: $tablet) {
            padding: 0 64px;
        }

        .filter-icon {
            flex-shrink: 0;
            margin-right: 16px;
        }

        .select {
            label {
                display: block;
                margin-bottom: 16px;
            }
        }
    }

    form {
        display: flex;
        flex-direction: column;

        label {
            display: block;
            margin-bottom: 12px;
        }
    }
</style>
